import React, { Component, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { Canceler } from 'axios';
import history from 'utils/history';

import { cancel } from 'utils/request';

import { actions as appActions } from 'reducer/app';
import { adasActions } from 'reducer/adas';
import { IRootState } from 'reducer';
import { checkModule } from 'shared/utils/appModules'

import { GlobalStyle }       from 'globalStyles';
import { Dispatch }          from 'redux';
import CustomSpinner         from './views/components/UI/Spinner';
import { menuItems, routes } from './routes'
import { checkRead }         from './utils/permissions';
import { pageRead }          from './api/app';
import handbooks             from './views/pages/Handbooks/SpreadSheetHandbook/configs';

const Layout = lazy(() => import('views/components/UI/Layout'));
const AuthPage = lazy(() => import('views/pages/AuthPage'));

const Page404 = lazy(() => import('views/pages/CatchingPages/Page404'));
const Page403 = lazy(() => import('views/pages/CatchingPages/Page403'));

const ServerError = lazy(() => import('views/pages/CatchingPages/ServerError'));
const ClientError = lazy(() => import('views/pages/CatchingPages/ClientError'));

const cancelRequests = (
  cancels: Array<{ c: Canceler; reqPath: string[] }>,
  currentPath: string
) => {
  cancels &&
    cancels.forEach(
      (cancel) =>
        cancel.c &&
        !cancel.reqPath.includes(currentPath) &&
        !currentPath.includes(cancel.reqPath[0]) &&
        cancel.c()
    );
};

interface IProps {
  availablePath: string;
  isLogged: boolean;
  isInitializing: boolean;
  toggleFullscreen: (value: boolean) => void;
  setAdasAvailabilityFlag: (value: boolean) => void;
  performInitialAppLoading: () => void;
  isLogoutLoading: boolean;
}

interface IState {
  error: boolean;
  errorMessage?: string;
}

class App extends Component<IProps, IState> {
  state: IState = {
    error: false,
  };

  historyUnregister: VoidFunction | null = null;

  componentDidMount(): void {
    const { toggleFullscreen, setAdasAvailabilityFlag, performInitialAppLoading } = this.props;
    this.historyUnregister = history.listen((location) => {
      cancelRequests(cancel, location.pathname);
      toggleFullscreen(false);
      this.setState({
        error: false,
        errorMessage: undefined,
      });

      if (!this.props.isLogoutLoading) {
        let route;

        if (location.pathname.includes('handbooks')) {
          const chunks = location.pathname.split('/');
          const handbook = handbooks[chunks[chunks.length - 1]];
          if (handbook) {
            route = {
              title: handbook.title
            }
          }
        } else if (location.pathname.includes('users/edit')) {
          route = {
            title: 'Редактирование пользователя',
          }
        } else if (location.pathname.includes('roles/edit')) {
          route = {
            title: 'Редактирование роли',
          }
        } else if (location.pathname.includes('smsv/feed')) {
          route = {
            title: 'Система мониторинга состояния водителя. Лента событий',
          }
        } else if (location.pathname.includes('smsv/reports')) {
          route = {
            title: 'Система мониторинга состояния водителя. Отчёты',
          }
        } else if (location.pathname.includes('smsv/driver-card')) {
          route = {
            title: 'Система мониторинга состояния водителя. Карточка водителя',
          }
        } else if (location.pathname.includes('smsv/import-manager')) {
          route = {
            title: 'Система мониторинга состояния водителя. Менеджер отчётов',
          }
        } else {
          route = routes.find(({ path }) => path === location.pathname);
        }

        if (route && route.title) {
          pageRead({ title: route.title, url: window.location.href, result: true });
        }
      }
    });

    if (process.env.REACT_APP_KSHS_CLIENT) {
      const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (!link) {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.href = "/icons/KSHS_tab_logo.png";
        document.head.appendChild(newLink);
      } else {
        link.href = "/icons/KSHS_tab_logo.png";
      }
    }

    performInitialAppLoading();
    setAdasAvailabilityFlag(checkModule('adas'));
  }

  componentWillUnmount(): void {
    this.historyUnregister && this.historyUnregister();
  }

  componentDidCatch(error: Error): void {
    this.setState({
      error: true,
      errorMessage: error.message,
    });
  }

  render() {
    const { isLogged, isInitializing, availablePath } = this.props;
    const { error } = this.state;

    return (
      <>
        <GlobalStyle />

        {
          isInitializing
          ? <CustomSpinner />
          : <Switch>
            <Suspense fallback={<CustomSpinner />}>
              {
                !isLogged &&
                <>
                  <Route exact path={'/'} component={AuthPage} />
                  <Redirect path={'/'} to={'/'} />
                </>
              }
              {isLogged && (
                <Layout>
                  <Switch>
                    {error && <Redirect to={'/client-error'} />}

                    { routes.map(({ path, component: C, alwaysAvailable }, index) => {
                                   return <Route
                                     path={path}
                                     key={index}
                                     render={(props) => {
                                       if (checkRead(path) || alwaysAvailable) {
                                         return <C {...props} />;
                                       }
                                       return <Redirect to={'/access-denied'} />;
                                     }}
                                   />;
                                 }
                    )}

                    <Route path={'/access-denied'} component={Page403} />
                    <Route path={'/server-error'} component={ServerError} />
                    <Route path={'/client-error'} component={ClientError} />
                    <Route path={'/not-found'} component={Page404} />

                    <Redirect exact path={'/'} to={availablePath} />
                    <Redirect path={'/'} to={'/not-found'} />
                  </Switch>
                </Layout>
              )}
            </Suspense>
          </Switch>
        }
      </>
    );
  }
}

const mapActionToProps = (dispatch: Dispatch) => ({
  toggleFullscreen: (value: boolean) =>
                      dispatch(appActions.toggleFullscreen({ value })),
  setAdasAvailabilityFlag: (value: boolean) =>
                      dispatch(adasActions.setAvailabilityAdas(value)),
  performInitialAppLoading: () =>
                      dispatch(appActions.performInitialAppLoading()),
});

const mapStateToProps = ({ user, app }: IRootState) => ({
  availablePath: user.availablePath,
  isLogged: user.isLogged,
  isInitializing: app.isInitializing,
  isLogoutLoading: user.isLogoutLoading,
});

export default connect(mapStateToProps, mapActionToProps)(App);
